// Terrain: chunks, the client's chunk map, and a zone's terrain as the world sends it.
import { GameConstants, GroundType } from '../../../shared/src/constants';
import { InvalidDataError, inflate, readUint64LE, sha256 } from './bytes';
import { tiles, content } from './content';
import { entityIsSolid, type PlacedEntity } from '../../../shared/src/entityGeometry';
import { ObjectSpills, TileCatalog } from '../../../shared/src/content';
import type { CollisionMap } from '../../../shared/src/motor';

export const chunkSize = GameConstants.chunkSize;
export const tilesPerChunk = chunkSize * chunkSize;

export const chunkOfTile = (t: number) => Math.floor(t / chunkSize);
/** A chunk coordinate as a map key. */
export const chunkKey = (cx: number, cy: number) => `${cx},${cy}`;
export const chunkOfPixel = (p: number) => chunkOfTile(Math.floor(p / GameConstants.tileSize));

export class Chunk {
  constructor(readonly cx: number, readonly cy: number,
    readonly ground = new Uint16Array(tilesPerChunk), readonly ground2 = new Uint16Array(tilesPerChunk),
    readonly ground3 = new Uint16Array(tilesPerChunk), readonly objects = new Uint16Array(tilesPerChunk)) {}

  /** Ground layers 1-3, then the one object grid (the terrain wire's order). */
  get layers(): Uint16Array[] { return [this.ground, this.ground2, this.ground3, this.objects]; }
  changeObject(i: number, object: number): void { this.objects[i] = object; }

  static index(lx: number, ly: number): number { return ly * chunkSize + lx; }
}

/** Loaded chunks; unloaded tiles (and anything outside the zone) are solid, so nothing walks into unknown space. */
export class ChunkMap implements CollisionMap {
  private readonly chunks = new Map<string, Chunk>();
  /** Tiles blocked by solid tile entities (a workbench's footprint), as "x,y". */
  readonly blockers = new Set<string>();
  readonly placedEntities = new Map<string, PlacedEntity>();
  /** Loaded objects whose collision reaches past their tile, by every tile it touches (rebuilt as chunks change). */
  private readonly spills: ObjectSpills;

  constructor(readonly widthTiles: number, readonly heightTiles: number) { this.spills = new ObjectSpills(widthTiles, heightTiles); }

  get count(): number { return this.chunks.size; }
  all(): IterableIterator<Chunk> { return this.chunks.values(); }
  inBounds(tx: number, ty: number): boolean { return tx >= 0 && ty >= 0 && tx < this.widthTiles && ty < this.heightTiles; }
  get(cx: number, cy: number): Chunk | undefined { return this.chunks.get(chunkKey(cx, cy)); }
  has(cx: number, cy: number): boolean { return this.chunks.has(chunkKey(cx, cy)); }
  set(chunk: Chunk): void { this.chunks.set(chunkKey(chunk.cx, chunk.cy), chunk); this.indexSpills(); }
  remove(cx: number, cy: number): boolean { const removed = this.chunks.delete(chunkKey(cx, cy)); if (removed) this.indexSpills(); return removed; }

  private indexSpills(): void {
    const catalog = tiles();
    this.spills.clear();
    for (const c of this.chunks.values())
      for (let i = 0; i < tilesPerChunk; i++) if (c.objects[i] && catalog.objectSpill(c.objects[i]))
        this.spills.add(c.cx * chunkSize + i % chunkSize, c.cy * chunkSize + Math.floor(i / chunkSize), c.objects[i], catalog);
  }

  /** [ground, object] of a tile, or null if it isn't loaded. */
  tile(tx: number, ty: number): [number, number] | null {
    if (!this.inBounds(tx, ty)) return null;
    const cx = chunkOfTile(tx), cy = chunkOfTile(ty);
    const c = this.chunks.get(chunkKey(cx, cy));
    if (!c) return null;
    const i = Chunk.index(tx - cx * chunkSize, ty - cy * chunkSize);
    return [c.ground3[i] || c.ground2[i] || c.ground[i], c.objects[i]];
  }

  ground(tx: number, ty: number): number { return tiles().baseGround(this.tile(tx, ty)?.[0] ?? GroundType.Void); }
  object(tx: number, ty: number): number { return this.tile(tx, ty)?.[1] ?? 0; }

  /** Changes one tile in a loaded chunk; false if it isn't loaded. */
  setTile(tx: number, ty: number, ground: number, object: number): boolean {
    if (!this.inBounds(tx, ty)) return false;
    const cx = chunkOfTile(tx), cy = chunkOfTile(ty);
    const c = this.chunks.get(chunkKey(cx, cy));
    if (!c) return false;
    const i = Chunk.index(tx - cx * chunkSize, ty - cy * chunkSize);
    c.ground[i] = ground;
    const spilled = tiles().objectSpill(c.objects[i]) || tiles().objectSpill(object);
    c.changeObject(i, object);
    if (spilled) this.indexSpills();
    return true;
  }

  isSolidTile(tx: number, ty: number): boolean {
    const t = this.tile(tx, ty);
    if (t === null) return true;
    const c = this.get(chunkOfTile(tx), chunkOfTile(ty))!;
    const i = Chunk.index(tx % chunkSize, ty % chunkSize);
    const r = tiles();
    return (c.ground[i] === 0 && c.ground2[i] === 0 && c.ground3[i] === 0) ||
      (c.ground[i] !== 0 && r.isSolidGround(c.ground[i])) || (c.ground2[i] !== 0 && r.isSolidGround(c.ground2[i])) ||
      (c.ground3[i] !== 0 && r.isSolidGround(c.ground3[i])) || r.isSolidObject(c.objects[i]) || this.blockers.has(`${tx},${ty}`) ||
      [...this.placedEntities.values()].some(e => { const d = content().entity(e.definition); return d !== undefined && entityIsSolid(e, d, e.depleted, tx, ty); });
  }

  partialBoxes(tx: number, ty: number, out: number[]): void {
    if (!this.inBounds(tx, ty)) return;
    const c = this.get(chunkOfTile(tx), chunkOfTile(ty));
    if (!c) return;
    const i = Chunk.index(tx % chunkSize, ty % chunkSize), r = tiles();
    for (const ground of [c.ground[i], c.ground2[i], c.ground3[i]]) TileCatalog.place(r.groundBoxes(ground), tx, ty, out);
    if (!r.objectSpill(c.objects[i])) TileCatalog.place(r.objectBoxes(c.objects[i]), tx, ty, out);
    this.spills.appendBoxes(tx, ty, r, out);
  }
}

/** Largest map accepted (8192 x 8192 tiles). */
const maxTiles = 8192 * 8192;

/** A zone's terrain as it was made (without what's changed since: that travels with each chunk). */
export class MapTerrain {
  private constructor(readonly width: number, readonly height: number, readonly hash: bigint, readonly ground: Uint16Array,
    readonly ground2: Uint16Array, readonly ground3: Uint16Array, readonly objects: Uint16Array) {}

  /** Where a client keeps a terrain it was sent. */
  static cacheName(hash: bigint): string { return `terrain/${hash.toString(16).padStart(16, '0')}.bin`; }

  /**
   * Reads terrain: int32 marker -5, width, height, then four raw-deflated LE u16 grids (ground 1-3, objects). The hash
   * covers the exact serialized header and grids.
   */
  static async decode(data: Uint8Array): Promise<MapTerrain> {
    const headerSize = 12;
    if (data.length < headerSize) throw new InvalidDataError('terrain cut short');
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    if (view.getInt32(0, true) !== -5) throw new InvalidDataError('unsupported terrain version');
    const w = view.getInt32(4, true), h = view.getInt32(8, true);
    if (w <= 0 || h <= 0 || w * h > maxTiles || w > 8192 || h > 8192) throw new InvalidDataError(`bad terrain size ${w}x${h}`);
    const count = w * h;
    const layers = await inflate(data.subarray(headerSize), 8 * count);
    if (layers.length !== 8 * count) throw new InvalidDataError('incorrect terrain layer length');
    const hashed = new Uint8Array(headerSize + layers.length);
    hashed.set(data.subarray(0, headerSize)); hashed.set(layers, headerSize);
    const hash = readUint64LE(await sha256(hashed));
    const pixels = new DataView(layers.buffer, layers.byteOffset, layers.byteLength);
    const layer = (n: number) => {
      const ids = new Uint16Array(count);
      for (let i = 0; i < count; i++) ids[i] = pixels.getUint16((n * count + i) * 2, true);
      return ids;
    };
    return new MapTerrain(w, h, hash, layer(0), layer(1), layer(2), layer(3));
  }

  chunkInBounds(cx: number, cy: number): boolean {
    return cx >= 0 && cy >= 0 && cx * chunkSize < this.width && cy * chunkSize < this.height;
  }

  /** One chunk of it (a copy). */
  chunkAt(cx: number, cy: number): Chunk {
    const chunk = new Chunk(cx, cy);
    if (!this.chunkInBounds(cx, cy)) return chunk;
    const count = Math.min(chunkSize, this.width - cx * chunkSize);
    for (let ly = 0; ly < chunkSize && cy * chunkSize + ly < this.height; ly++) {
      const src = (cy * chunkSize + ly) * this.width + cx * chunkSize;
      for (const [n, l] of [this.ground, this.ground2, this.ground3, this.objects].entries())
        chunk.layers[n].set(l.subarray(src, src + count), ly * chunkSize);
    }
    return chunk;
  }
}
